import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'i18n';
import { AuthActions } from 'store/actions';
import { AuthSelectors } from 'store/selectors';
import { useModal } from 'hooks/index';
import AuthErrorModal from 'components/auth/AuthErrorModal';
import { JLIFE_FALLBACK_ERROR_KEY, parseJlifeError, toInternalPath } from 'utils/jlifeAuth';

// Asks the API for the JLife sign-in link and redirects the browser to it (see handleJlifeAuthorizeRequest).
// If the API can't give the link (e.g. 503), the user sees an error instead of a button that does nothing.
const useJlifeSignIn = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation('public');
  const [showErrorModal] = useModal(AuthErrorModal);
  const { isPending, error } = useSelector(AuthSelectors.jlifeAuthorizeState);
  // Many components use this hook; only the one that started the sign-in reports its failure.
  const hasStartedRef = useRef(false);

  const signIn = useCallback(
    (destination?: string, options?: { passportFlow?: boolean }) => {
      const path = toInternalPath(destination);
      hasStartedRef.current = true;
      dispatch(
        AuthActions.jlifeAuthorize.request({
          params: {
            ...(path && { destination: path }),
            ...(options?.passportFlow && { passport_flow: 'true' as const }),
          },
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (!hasStartedRef.current || !error) return;

    hasStartedRef.current = false;
    showErrorModal({
      error: parseJlifeError(error).description || t<string>(JLIFE_FALLBACK_ERROR_KEY),
      onClose: () => dispatch(AuthActions.jlifeAuthorize.reset()),
    });
  }, [error, showErrorModal, dispatch, t]);

  return { signIn, isPending };
};

export default useJlifeSignIn;
